import { IonIcon, IonRouterLink } from '@ionic/react'
import { ellipse, home, optionsOutline, shareOutline } from 'ionicons/icons'
import React from 'react'

import { APP_STORE_URL } from './definitions'
import { DisplayUpgradeOffer, PlanName } from './Plan'
import { NavItem, OpenClubLink } from './Help'
import { MoreInfoLink } from './MoreInfoLink'
import SampleGoButton from './SampleGoButton'
import {FlowInviteGallery} from './FlowInviteUpsell'
import { loadFromLocalStorage } from './util'
import DownloadOnTheAppStoreImage from './media/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg'
import playHQImage from './media/playhq.png'
import comboImg from './media/combos.png'
import subDemoImg from './media/sub-demo.gif'
import subDemoClip from './media/sub-demo.mp4'
import subDemoClip2 from './media/sub-demo-2.mp4'
import FillinIcon from './FillinIcon'
import {HiDotsHorizontal} from 'react-icons/hi'
import {TiTimes} from 'react-icons/ti'
import {FaCalendarTimes, FaStreetView} from 'react-icons/fa'
import {training} from './media/trainingIcon'

export const newsItems: NewsItem[] = [
    {
        date: '5 November 2026',
        content: () => (
            <>
                <p>
                    <b>New in 2.17.7</b>: Support for 6-player formats with no C. Who knew there were
                    so many netball formats? Not me. Not until people started emailing to ask why
                    AutoRoster doesn't support their netball format. But I do like the sound of 6 players
                    with no C, since that would mean I never have to play C. That position is <em>hard</em>.
                </p>
                <p>
                    For more info, see
                    {' '}
                    <MoreInfoLink helpSection="tips" helpSubSection="format" text="Tips & Tricks → Format" />.
                </p>
            </>
        ),
    },
    {
        date: '21 July 2026',
        content: () => (
            <>
                <p>
                    <em>New in version 2.17.0:</em> <b>More flexible subs!</b>
                </p>
                <div className="video-container">
                    <video playsInline autoPlay muted loop>
                        <source src={subDemoClip2} type="video/mp4" />
                    </video>
                </div>
                <p>
                    You may now combine any two roles in a single quarter, not just
                    players moving to/from the bench.
                </p>
                <p>
                    <b>Activate via long-press!</b> This is a change &mdash;
                    previously you had to tap a couple of times, then wait.
                </p>
                <p>
                    You now create basic subs like this:
                </p>
                <div className="video-container">
                    <video playsInline autoPlay muted loop>
                        <source src={subDemoClip} type="video/mp4" />
                    </video>
                </div>
                <p>
                    <MoreInfoLink helpSection="tips" helpSubSection="subs" text="More about subs..." />
                </p>
                <p>
                    This update also has many little roster UI fixes
                    for a smoother rostering experience.
                </p>
            </>
        ),
    },

    {
        date: '7 July 2026',
        content: () => (
            <>
                <p>
                    <em>New in version 2.16.0:</em> <b>Fixture Sync for PlayHQ / NetballHQ</b>!
                    Free yourself from the tyranny of having to manually enter games and results &mdash; instead,
                    hook AutoRoster up and let it update automatically.
                </p>
                <p>
                    <img src={playHQImage} alt="" />
                </p>
				<p>
					This is a feature that NetballConnect leagues have enjoyed for a while.
					It's still marked "Beta" because it relies on third-party data,
					but once you try it, you can't go back.
				</p>
				<p>
					For existing seasons, add Fixture Sync via Season settings at the bottom of
                    your <NavItem tab="games" /> tab. For new seasons, look out for the "Connect..." button.
                    Fixture Sync always offers you the chance to accept or reject each fixture update.
                </p>
				<p>
                    <em>Fixture Sync for PlayHQ requires an app version of AutoRoster &mdash; not a web browser.</em>
                </p>
            </>
        ),
    },

    {
        date: '24 June 2026',
        content: () => (
            <>
                <p>
					<b>New in version 2.15.0</b>: Flow Maps! <span className="keep-together">(<PlanName /></span> only.)
                </p>
                <FlowInviteGallery />
                <p>
                    Flow Map is a ball movement analysis tool that:
                </p>
                <ol>
					<li>
						<p>
							Has an easy-to-use, low-stress recorder &mdash; rope in anyone
							to record games even if they don't know much about netball, or who the players are;
						</p>
					</li>
                    <li>
                        <p>
                            Delivers rich ball movement stats that help coaches identify what happened when, where, and why.
                        </p>
                    </li>
                </ol>
                <p>
                    A coach can send a link to someone, who just needs to tap their phone on a
                    turnover or goal, and that's all AutoRoster needs to generate a ball movement analysis.
                </p>
				<p>
					It's not individual player stats tracking &mdash; e.g. no goalshooter accuracy. But I think
					it's new and useful.
				</p>
                <p>
                    <IonRouterLink routerLink="/help/game-insight">More about Flow Maps...</IonRouterLink>
                </p>
                <p>
                    Flow Maps are an <PlanName /> feature (free trial available to new subscribers).
                </p>
                <DisplayUpgradeOffer />
            </>
        ),
    },

    {
        date: '4 June 2026',
        content: () => (
            <>
                <p>
                    <b>New in 2.14.15</b>: <em>6-a-side (no WD)</em> game format, which Tania S. tells me
                    is a New Zealand thing for kids who progress from the 6-a-side (A/C/D) format.
                    As opposed to some kind of personal slight on WDs, which I would not endorse.
                    Good WDs are gold.
                </p>
                <p>
                    For more info, see
                    {' '}
                    <MoreInfoLink helpSection="tips" helpSubSection="format" text="Tips & Tricks → Format" />.
                </p>
            </>
        ),
    },

    {
        date: '29 May 2026',
        content: () => (
            <>
                <p>
                    <PlanName /> now offers a 30-day trial for new subscribers,
                    so you can check it out for free. I'm releasing a new
                    stats feature very soon for Pro users, but more on that later!
                </p>
                <p>
                    Thank you everyone who supports my development of this app!
                </p>
                <MoreInfoLink helpSection="autorosterpro" text="More about AutoRoster Pro..." />
            </>
        ),
    },

    {
        date: '10 April 2026',
        content: () => (
            <>
                <p>
                    <b>New in version 2.14.0</b>: serious improvements to roster generation,
                    so AutoRoster is smarter than ever when you hit <SampleGoButton />.
                </p>
                <p>
                    <b>Things have changed!</b> Read on,
                    and take a moment to inspect your roster settings, especially if you
                    use <b><NavItem icon={optionsOutline} label="" /> Custom</b>. Some
                    sliders have gone away, and some behave differently.
                </p><p>
                    Consider switching to a
                    default preset (Development, Junior, or Senior), and tweaking sliders
                    from there as needed.
                </p>
                <p>
                    Changes in <NavItem icon={optionsOutline} label="" /> roster settings:
                </p>
                <ul>
                    <li>
                        <p>
                            <b>Use rolling subs</b> (new slider): Set how happy you want to be with
                            mid-quarter substitutions. If you let it, AutoRoster will use subs
                            to better meet your roster priorities.
                            <MoreInfoLink helpSection="tips" helpSubSection="subs" text="More about subs..." />
                        </p>
                    </li>
                    <li>
                        <p>
                            <b>Avoid exhaustion</b> (changed): Now accounts for the physical demands of each
                            position, not just Centres. But don't worry, Centres, it's
                            still mostly about you.
                        </p>
                    </li>
                    <li>
                        <p>
                            <b>Copy previous game</b> &amp; <b>Prefer similar positions</b> (removed sliders):
                            Previously these sliders were handy for gradually easing young players into new positions,
                            but they're no longer necessary as that happens automatically with
                            the <NavItem icon={optionsOutline} label="" /> Development preset or
                            similar slider settings.
                        </p>
                    </li>
                </ul>
                <p>
                    Changes to <FaStreetView /> <b>Positions</b> preferences:
                    <MoreInfoLink helpSection="tips" helpSubSection="prefs" text="More about Positions preferences..." />
                </p>
                <ul>
                    <li>
                        <p>
                            <b>Prefer</b> mode means "these players are better in <b>any</b> of these positions."
                            AutoRoster will try to put them there rather than somewhere else, but will no longer try
                            to assign the player <em>all</em> nominated positions every game.
                        </p>
                    </li>
                    <li>
                        <p>
                            <b>Require</b> mode means "these players must play <b>all</b> of these positions."
                            This can be pretty radical, especially if you set a lot of them, so AutoRoster will
                            drop a <span className="red">*</span> red warning asterisk on your roster page.
                        </p>
                    </li>
                </ul>
                <p>
                    When you tap <SampleGoButton />, you might notice:
                </p>
                <ul>
                    <li>
                        <p>
                            It changes to "Stop," and you can tap again to abort roster generation.
                        </p>
                    </li>

                    <li>
                        <p>
                            Roster generation is faster and less prone to getting stuck on a bad
                            pattern. (Previously, sometimes you needed to erase a mediocre
                            roster to let AutoRoster generate a better one.)
                        </p>
                    </li>
                </ul>
                <p>
                    The net effect (heh) of all this is AutoRoster v2.14.x is quite a lot more effective
                    on default settings. So when you create a new team, this is a good process:
                </p>
                <ol>
                    <li>
                        <p>
                            Choose an appropriate roster
                            setting: <NavItem icon={optionsOutline} label="" /> <b>Development</b> for
                            giving young players a taste of all different
                            positions; <NavItem icon={optionsOutline} label="" /> <b>Junior</b> for
                            balancing team performance and participation;
                            and <NavItem icon={optionsOutline} label="" /> <b>Senior</b> for
                            experienced specialists.
                        </p>
                    </li>
                    <li>
                        <p>
                            For your first game:
                        </p>
                        <ul>
                            <li>
                                <p>
                                    Optionally, set some <FaStreetView /> <b>Positions</b> preferences
                                    for fine-grained control over who should play where. But if you're not
                                    too fussed, skip right ahead to the next step.
                                </p>
                            </li>
                            <li>
                                <p>
                                    Tap <SampleGoButton /> to let AutoRoster generate a roster. To swap entire
                                    rows, drag player names; or drag individual positions.
                                </p>
                            </li>
                        </ul>
                    </li>
                </ol>
                <p>
                    If you have a roster that refuses to come out right, let me know! Use the "Email Max"
                    button at the bottom of this screen. Be sure to tell me what roster settings you're using:
                    Development, Junior, Senior, or Custom.
                </p>
			</>
		),
	},
    {
		date: '8 April 2026',
		content: () => (
			<>
				Fixture Sync suffered a bit of a hit last week, as the data it depends on from NetballConnect
                was gated behind a login wall. That means it's a bit more painful when adding Fixture Sync
                to a new team than it used to be. Hopefully the rest of NetballConnect will stay public,
                because it's very handy!
                <br /><br />
                <MoreInfoLink helpSection={"tips"} helpSubSection={"sync"} text="More about Fixture Sync..." />
			</>
		),
	},
	{
		date: '18 March 2026',
		content: () => (
			<>
				<b>New in version 2.13.0:</b> Support for Mixed comps where rosters must
				follow a "1 male per third" rule. Now under "Rules" in your season settings!
				<br /><br />
				<MoreInfoLink helpSection={"tips"} helpSubSection={"rules"} text="More about Mixed Rules..." />
			</>
		),
	},
	{
		date: '10 March 2026',
		content: () => (
			<>
				<b>New in version 2.12.0:</b> Fixture Sync is very handy if you're in
				a NetballConnect league — it spares you from having to manually enter games
				and scores, and you even get shown when there's been a court or time change.
				<br /><br />
				But it can get painful if the official fixture isn't quite right, because
			    you have to choose between syncing everything or nothing.
				<br /><br />
				Until now! Now you can ignore certain games while syncing the rest. Look
				out for "Skip..." options the next time your Fixture Sync pops up.
				<br /><br />
				<MoreInfoLink helpSection={"tips"} helpSubSection={"sync"} text="More about Fixture Sync..." />
			</>
		),
	},
	{
		date: '15 February 2026',
		content: () => (
			<>
				<b>New in version 2.11.0:</b> You can specify how many different positions
				your players should ideally have.
				<br /><br />
				This is my third attempt at implementing this feature since 2022, because
				there's a subtle difference between <em>how many</em> positions your
				players should have, and <em>how important</em> it is for AutoRoster to
				meet that number.
				<br /><br />
				Hopefully it's intuitive this time! You can find it
				in <NavItem icon={optionsOutline} label="Settings" /> via the Edit mode of
				any roster.
			</>
		),
	},
	{
		date: '27 January 2026',
		content: () => (
			<>
				<b>New in version 2.10.0:</b> <IonIcon icon={training} className="help-training-icon" /> Training Attendance! And Team Notes!
				<br /><br />
				As every coach knows, there are players who never miss training, and
				players who are constantly called away at the last moment to deal with
				emergencies beyond their control.
				<br /><br />
				Now you can collect hard data on who's who! The new <NavItem tab="training" /> tab
				lets you quickly mark attendance for a training session, with a season summary
				available in <NavItem tab="stats" /> → <FaCalendarTimes /> <b>Attendance</b> → <b>Training</b>.
				<br /><br />
				And that's not all! Some coaches wanted to be able to jot down notes
				in the app. Now you can do that, too, also via the Training tab. Use it for
				ideas, reminders, or anything else you want to keep track of.
				Notes are private to you and any team co-coaches and club admins.
				<br /><br />
				There's also a performance improvement when auto-generating rosters.
			</>
		),
	},
	{
		date: '6 January 2026',
		content: () => (
			<>
				Happy New Year! Thanks to everyone using AutoRoster &mdash; I see more
				teams using it around the courts, which makes me happy.
				<br /><br />
				To support community clubs offering the benefits
				of <PlanName /> to their coaches, I've slashed the club subscription price in half &mdash; now just
			    A$29.99/month, or A$299/year.
				<br /><br />
				That means instead of individual coaches each paying out of their own pockets,
				clubs can grant all their coaches storage for multiple teams, multiple
				seasons, and access to the advanced stats on effective player combinations.
				<br /><br />
				<OpenClubLink />
			</>
		),
	},

	{
		date: '16 October 2025',
		content: () => (
			<>
				<b>v2.9.9</b>: There have been many little updates since 2.9.0 that haven't deserved their own post.
				Here are some of them!
				<ul>
					<li>
						Abandoned / Forfeit games: in <NavItem tab="games" />, tap <HiDotsHorizontal /> (three dots on a particular game) {'>'} Edit Game {'>'} Result.
					</li>
					<li>
						View Playtime: In <NavItem tab="stats" /> {'>'} Positions Played, tap the right-most column to toggle through
						Total Quarters Played, Total Quarters Benched, and % of game time spent on court.
					</li>
					<li>
						Stats for Fill-ins: When you designate a player as a Fill-in, they don't display in
						your <NavItem tab="stats" />, to avoid clutter. But what if you want to see them anyway?
						Now there's a toggle at the top of relevant stats pages (visible only when you have fill-ins).
					</li>
					<li>
						Import players from a previous season rather than re-entering them.
					</li>
					<li>
						Clubs admins can edit the club name.
					</li>
					<li>
						Plus a bunch of bugfixes and UI improvements, such as a clearer explanation for
						when you mysteriously can't edit your team any more! (It's because you signed in
						as a different user, who isn't a coach for that team.)
					</li>
				</ul>
			</>
		),
	},

	{
		date: '11 July 2025',
		content: () => (
			<>
				<b>New in version 2.9.0:</b> Fill-ins! <FillinIcon />
				<br /><br />
				You can now designate players as Fill-ins, so you can
				put their names into a roster, but they won't clutter
				up your team Stats.
				<br /><br />
				<NavItem tab="players" /> {'>'} <HiDotsHorizontal /> (three dots beside name of player) {'>'} <NavItem label="Edit Player" /> {'>'} This player is a fill-in <FillinIcon />
				<br /><br />
				The bird icon is because they flit in and out of your team.
				<br /><br />
				Also: Scrolling! This season I coached a team that
				wound up using 20 players, and boy did that make
				it hard to scroll. So now:
				<ul>
					<li>
						In Edit mode, Unavailable players
						(who have a <NavItem IconElement={TiTimes} iconClassName="cross" label="gray cross" /> beside their name,
						not a <NavItem icon={ellipse} iconClassName="tick" label="green dot" />) aren't draggable,
						nor are their bench positions.
					</li>
				</ul>
				That means you can scroll the page if you start with your
				finger on them.
				<br /><br />
				Some Unavailable players are now hidden by default.
			    You can reveal them with "+ 2 Players", which shows up where they'd normally be.
				<br /><br />
				A nice list of who filled in when is available in <NavItem tab="stats" /> {'>'} <NavItem IconElement={FaCalendarTimes} label="Leadership, Absentees & Fill-ins" /> {'>'} <b>Fill-ins</b>.
			</>
		),
	},
	{
		date: '8 May 2025',
		content: () => (
			<>
				<b>New in version 2.8.0:</b> Printing! You might think adding Print to an app would
				be simple. I thought that, too. But no. It's a whole thing.
				<br /><br />
				The web version of AutoRoster has always offered printing, but now you can do it in the native app
				as well.
				<br /><br />
				To print your roster — or save it as a PDF, which, it turns out, is basically the same
				thing — use the <IonIcon icon={shareOutline} /> Share link at the top of any roster page.
			</>
		),
	},
	{
		date: '3 January 2025',
		content: () => (
			<>
                <span className="error-subtitle">Update: This information is now obsolete! Create subs with long-press instead.</span>
                <MoreInfoLink helpSection="tips" helpSubSection="subs" text="More info..." />
                <br /><br />
				<b>New in version 2.7.0:</b> Subs! Yes, it's now possible to create rosters with substitutions,
				where a player comes off the bench to replace someone else mid-quarter.
				<br /><br />
				<img src={subDemoImg} alt="" className="subDemoImg" />
				<br /><br />
				To create a sub, enter <NavItem label="Edit" /> mode and tap a position twice so that it
				displays an arrow animation. Wait a second or two, and the position will split in two.
				To reverse this (i.e. turn a split position back into a full-quarter one), do the
				same thing again. <MoreInfoLink helpSection="tips" helpSubSection="subs" text="More about Subs..." />
			</>
		),
	},
	{
		date: '12 November 2024',
		content: () => (
			<>
				<b>New in version 2.6.0:</b> Support for 5- and 6-player games. This is mainly useful for Indoor
				Netball and New Zealand's <em>futureFERNS</em>. You can choose your Format when creating a new
				season.
				<ul>
					<li>
						<p>
							Standard: Seven players
						</p>
					</li>
					<li>
						<p>
							6-a-side: Two each of <b>A</b>, <b>C</b>, and <b>D</b>
						</p>
					</li>
					<li>
						<p>
							5-a-side: Two each of <b>A</b> and <b>D</b>, plus one <b>C</b>
						</p>
					</li>
					<li>
						<p>
							FAST5: No wings! Only GS, GA, C, GD, GK
						</p>
					</li>
				</ul>
			</>
		),
	},

	{
		date: '19 October 2024',
		content: () => (
			<>
				<b>New in version 2.5.0:</b> A big collection of bugfixes, tweaks and improvements, including:
				<ul>
					<li>
						<p>
							Season Fixture Sync (beta) - AutoRoster can now automatically pull
							quarter-by-quarter scores from NetballConnect, so you don't need to
							manually enter them. Quarter-by-quarter scores are key to most of the
							analysis AutoRoster can do, so this makes many stats much more accessible
							to coaches. (Only available when you've configured Fixture Sync in your
							Season settings for teams in NetballConnect-supported leagues.)
						</p>
					</li>
					<li>
						<p>
							Season Fixture Sync (beta) - Fix bug that could become confused by
							tournaments where teams play multiple games in quick succession.
						</p>
					</li>
					<li>
						<p>
							Clubs - Club admin can properly view Stats for club teams.
						</p>
					</li>
					<li>
						<p>
							Improved support for more devices.
						</p>
					</li>
					<li>
						<p>
							Code migration for improved reliability and futureproofing.
						</p>
					</li>
				</ul>
			</>
		),
	},

	{
		date: '22 April 2024',
		content: () => (
			<>
				<b>New in version 2.4.3:</b> Minor improvements to sharing and team selection.
				<br /><br />
				There's now a handy <IonIcon icon={shareOutline} /> Share link at the bottom of your Games list,
				and for busy coaches, AutoRoster will remember the last few teams/seasons you've viewed
				and offer them at the top of your team selector via the <IonIcon icon={home} /> Home button.
			</>
		),
	},

	{
		date: '3 April 2024',
		content: () => (
			<>
				<b>New in version 2.4.0:</b> Online fixture sync! If you play in a supported league,
				AutoRoster can automatically import your fixture and keep it up to date.
				<br /><br />
				Currently only the <b>NetballConnect</b> platform is supported, which mostly covers netball leagues
				in eastern Australia. But if that's you, it's super handy. Set it up via Season
				settings at the bottom of your <NavItem tab="games" />, or directly when creating
				a new season.
				<br /><br />
				Also:
				<br /><br />
				* If you toggle Positions between "Prefer" and "Require," AutoRoster will
				remember your choice in the future.
				<br /><br />
				* If you delete a season, AutoRoster won't
				let you add back games and players to it, to prevent your rosters becoming
				half-deleted zombies.
				<br /><br />
				* Fixed some minor bugs, including one where the dialog box would automatically
				close after entering a new player or game, which made it a chore to add lots of them.
			</>
		),
	},

	{
		date: '9 March 2024',
		content: () => (
			<>
				<b>New in version 2.3.4:</b> A fix for a bug that wouldn't tell you why it wasn't deleting
				your team, and one that could prevent people from joining your club.
				<br /><br />
				Some people have asked about rolling subs or tactical changes &mdash; how are you supposed to
				make rosters where players change mid-quarter? This is pretty tricky to support without making
				the app quite a lot more complicated, so unfortunately I don't have a great answer! I will
				probably look to add support for 5- and 6-player games (for Indoor Netball and New Zealand's
				futureFERNS Years 5 and 6 program) before tackling subs.
			</>
		),
	},

	{
		date: '11 December 2023',
		content: () => (
			<>
				<b>New in version 2.3.2:</b> Show the total number of quarters benched for each player by tapping the
				right-most column in <NavItem tab="stats" /> → <b>Positions Played</b>.
				<br /><br />
				Provide a subtle visual hint that a player should be marked as unavailable when they're
				assigned zero on-court roles in a roster.
			</>
		),
	},

	{
		date: '1 December 2023',
		content: () => (
			<>
				<b>New in version 2.3.1:</b> A bunch of small improvements and fixes, including squashing
				a bug that could crash on startup for rosters that aren't 4 quarters.
			</>
		),
	},
	{
		date: '12 November 2023',
		content: () => (
			<>
				<b>New in version 2.3.0:</b> Support for games that aren't 4 quarters.
				<br /><br />
				Quite a few people asked if they could customize the number of periods in a game.
				Now you can! Select from 1 to 6	periods by modifying your season settings,
				now conveniently available at the bottom of your Games list.
				<br /><br />
				For more info, see
                {' '}
                <MoreInfoLink helpSection="tips" helpSubSection="format" text="Tips & Tricks" /> → <b>Format (5- and 6-a-side)</b>.
				<br /><br />
				Additionally, when clubs create invitation links, those now last for 6 months
				before expiring, rather than 7 days, because I realized no-one is that organized.
			</>
		),
	},
	{
		date: '26 October 2023',
		content: () => (
			<>
				<b>New in version 2.2.0</b>: AutoRoster generates even better high-performance
				rosters by using data on combinations of players who work particularly well together.
				<br /><br />
				Previously, AutoRoster would only consider each player in isolation when
				scoring and generating rosters. Now it will actively seek out rosters that
				include pairs and trios that correlate with high performance, and avoid weaker ones.
				<br /><br />
				You can control how important AutoRoster considers stats
				with the <b>Optimize team performance</b> slider in
				your <NavItem icon={optionsOutline} label="Settings" /> in
				Edit mode on any roster.
				<br /><br />
				For <PlanName /> users, you can now see the strength of relevant Pairs and Trios
				directly on rosters, not just in <NavItem tab="stats" /> → <b>Combinations</b> like before.
				And if that's too many numbers, you can hide them in <NavItem tab="settings" />.
				<br /><br />
				Thanks for using AutoRoster, and good luck with your teams! &mdash; Max.
				<br /><br />
				<img
					className="comboImg"
					src={comboImg}
					alt="Combinations"
				/>
			</>
		),
	},
	{
		date: '24 July 2023',
		content: () => (
			<>
				I get a little map showing where this app is being used, and it's been very cool
				the last few months to see it spread across Australia, the UK, New Zealand and South Africa.
				<br /><br />
				Yet to make a meaningful impact in Jamaica, Malawi, Tonga, or Uganda, but maybe that will
				change with the release of the <b>Android version</b>! That should happen in
				the next week.
				<br /><br />
				Thanks for everyone who has taken the time to email me or rate the app on the App Store.
				The most commonly requested new features are:
				<ul>
					<li>Ability to enter game stats (e.g. shots taken by GA/GS)</li>
					<li>Variable number of game periods (e.g. 2 for tournaments rather than 4 qtrs)</li>
				</ul>
				At the moment I'm focused on making the app stable and bug-free, but after that,
				those will probably be the first I'll look at.
				<br /><br />
				Thanks for using and good luck with your teams! &mdash; Max.
			</>
		),
	},
	{
		date: '25 April 2023',
		content: () => (
			<>
				The site has undergone a big redesign to make it more mobile-friendly for
				court-side coaches. And there's an app!
				<br /><br />
				<a href={APP_STORE_URL}>
					<img src={DownloadOnTheAppStoreImage} alt="Download on the App Store" />
				</a>
				<br /><br />
				If you need the old site, you can find it <a href="https://v1.autoroster.io">here</a>.
				Please <a href="mailto:max_barry@maxbarry.com">email me</a> with any problems.
				<br /><br />
				This site and the app are still free to use, but you can subscribe to <PlanName /> for
				more data storage and extra stats.
				<br /><br />
				Thanks for using! &mdash; Max.
			</>
		),
	},
	{
		date: '13 August 2022',
		content: () => (
			<>
				You can now edit rosters in traditional roster format, with position names
				down the side and player names in columns. Tap <b>TOGGLE VIEW</b> and <b>EDIT</b>.
			</>
		),
	},
	{
		date: '7 June 2022',
		content: () => (
			<>
				The <b>POSITIONS</b> button now lets you set <em>preferred</em> positions
				for each player, rather than operating strictly as a this-person-must-play-here
				rule. I also fixed a bug that could produce stubborn, sub-optimal rosters
				that wouldn't improve.
			</>
		),
	},
	{
		date: '27 May 2022',
		content: () => (
			<>
				<b>NetStats!</b> Enter your game scores and let AutoRoster analyze which
				rosters delivered your best team performances. With this data, it can even generate rosters that optimize
				team strength.
			</>
		),
	},
]

export const newestNewsItemDate = newsItems[0].date

export const haveUnreadNewsItem = () => {
	const lastViewedNewsPage = loadFromLocalStorage('viewedNewsPage')
	if (typeof lastViewedNewsPage === 'string' || typeof lastViewedNewsPage === 'number') {
		return new Date(lastViewedNewsPage) < new Date(newestNewsItemDate)
	}
	return true
}
